import { useState, useCallback, useEffect, useRef } from 'react';
import { KEY_DOWN, KEY_RETURN, KEY_UP, KEY_ENTER, KEY_ESCAPE } from 'keycode-js';
import { isEmpty } from 'lodash';

export const useDropdownNavigation = ({ isOpen, options = [], onSelect, closeDropdown, onInputChange, listRef }) => {
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef(null);

    const handleInputChange = useCallback(
        newValue => {
            onInputChange(newValue);
            setHighlightedIndex(-1);
        },
        [onInputChange]
    );

    const handleKeyDown = useCallback(
        e => {
            if (isEmpty(options)) {
                return;
            }
            switch (e.keyCode) {
                case KEY_DOWN:
                    e.preventDefault();
                    e.stopPropagation();
                    setHighlightedIndex(prev => Math.min(prev + 1, options.length - 1));
                    break;
                case KEY_UP:
                    e.preventDefault();
                    e.stopPropagation();
                    setHighlightedIndex(prev => Math.max(prev - 1, 0));
                    break;
                case KEY_ENTER:
                case KEY_RETURN:
                    e.preventDefault();
                    if (options[highlightedIndex]) {
                        onSelect?.(options[highlightedIndex]);
                        closeDropdown();
                    }
                    break;
                case KEY_ESCAPE:
                    e.preventDefault();
                    closeDropdown();
                    break;
                default:
                    break;
            }
        },
        [options, highlightedIndex, onSelect, closeDropdown]
    );

    useEffect(() => {
        if (isOpen && inputRef?.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [options]);

    useEffect(() => {
        if (listRef?.current && options.length > 0 && highlightedIndex >= 0) {
            listRef.current.scrollToItem(highlightedIndex, 'auto');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedIndex, listRef]);

    return {
        highlightedIndex,
        setHighlightedIndex,
        inputRef,
        handleInputChange,
        handleKeyDown,
    };
};
